import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import comisionesService, {
  ComisionLiquidacionDetalle,
  getComisionErrorMessage,
  importeBaseDocumento,
  labelImporteBase,
} from '../../../services/comisionesService';
import { formatCurrency } from '../../../utils/formatters';
import { formatFechaComision } from '../liquidacionComisionesFormat';

interface LiquidacionComisionDetalleDialogProps {
  nroFormulario: number | null;
  onClose: () => void;
}

const InfoItem: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 500 }}>
      {value}
    </Typography>
  </Box>
);

const LiquidacionComisionDetalleDialog: React.FC<LiquidacionComisionDetalleDialogProps> = ({
  nroFormulario,
  onClose,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [detalle, setDetalle] = useState<ComisionLiquidacionDetalle | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!nroFormulario || nroFormulario <= 0) {
      setDetalle(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setDetalle(null);
    comisionesService
      .getLiquidacion(nroFormulario)
      .then((data) => {
        if (!cancelled) setDetalle(data);
      })
      .catch((error) => {
        if (cancelled) return;
        enqueueSnackbar(getComisionErrorMessage(error, 'Error al cargar el detalle de la liquidación'), {
          variant: 'error',
        });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [nroFormulario, enqueueSnackbar]);

  const cabecera = detalle?.cabecera;
  const documentos = detalle?.documentosOrigen ?? [];
  const comisionBase = documentos.find((d) => d.comisionBase)?.comisionBase ?? null;
  const conceptos = [
    { desc: detalle?.concCom1, imp: detalle?.concComImp1 },
    { desc: detalle?.concCom2, imp: detalle?.concComImp2 },
  ].filter((c) => (c.desc ?? '').trim() || (Number(c.imp) || 0) !== 0);

  const handleVerFactura = () => {
    if (!nroFormulario) return;
    navigate(`/compras/facturas/${nroFormulario}`);
  };

  return (
    <Dialog open={nroFormulario != null && nroFormulario > 0} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, color: '#1976d2' }}>
        Liquidación {cabecera?.formularioDisplay?.trim() || (nroFormulario ? `#${nroFormulario}` : '')}
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : !detalle ? (
          <Typography color="text.secondary" sx={{ py: 2 }}>
            No se pudo obtener el detalle de la liquidación.
          </Typography>
        ) : (
          <Stack spacing={3}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <InfoItem label="Fecha" value={formatFechaComision(cabecera?.fechaEmision)} />
              </Grid>
              <Grid item xs={6} md={3}>
                <InfoItem label="Comprobante" value={cabecera?.formularioDisplay?.trim() || '—'} />
              </Grid>
              <Grid item xs={6} md={3}>
                <InfoItem
                  label="Vendedor"
                  value={
                    cabecera?.nombreVendedor?.trim() ||
                    (cabecera?.nroVendedor != null ? `#${cabecera.nroVendedor}` : '—')
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InfoItem
                  label="Proveedor"
                  value={
                    cabecera?.nombreProveedor?.trim() ||
                    (cabecera?.nroProveedor != null ? `#${cabecera.nroProveedor}` : '—')
                  }
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InfoItem
                  label="Cuenta comisión"
                  value={cabecera?.nroCuentaComision != null ? cabecera.nroCuentaComision : '—'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InfoItem label="Total" value={formatCurrency(cabecera?.total ?? 0)} />
              </Grid>
              {cabecera?.descripcion?.trim() && (
                <Grid item xs={12} md={6}>
                  <InfoItem label="Descripción" value={cabecera.descripcion.trim()} />
                </Grid>
              )}
            </Grid>

            {conceptos.length > 0 && (
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Conceptos adicionales
                </Typography>
                <Stack spacing={0.5}>
                  {conceptos.map((c, idx) => (
                    <Stack key={idx} direction="row" justifyContent="space-between">
                      <Typography variant="body2">{c.desc?.trim() || '—'}</Typography>
                      <Typography variant="body2">{formatCurrency(c.imp ?? 0)}</Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Comprobantes liquidados ({documentos.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Fecha</TableCell>
                      <TableCell>Comprobante</TableCell>
                      <TableCell>Cliente</TableCell>
                      <TableCell align="right">{labelImporteBase(comisionBase)}</TableCell>
                      <TableCell align="right">Comisión</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documentos.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                          <Typography color="text.secondary">Sin comprobantes de origen.</Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      documentos.map((doc) => (
                        <TableRow key={doc.nroFormulario} hover>
                          <TableCell>{formatFechaComision(doc.fechaEmision)}</TableCell>
                          <TableCell>{doc.formularioDisplay?.trim() || '—'}</TableCell>
                          <TableCell>
                            {doc.nombreCliente?.trim() ||
                              (doc.nroCliente != null ? `#${doc.nroCliente}` : '—')}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(importeBaseDocumento(doc, comisionBase))}
                          </TableCell>
                          <TableCell align="right">{formatCurrency(doc.impComision ?? 0)}</TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>
          Cerrar
        </Button>
        <Button
          variant="contained"
          onClick={handleVerFactura}
          disabled={!nroFormulario}
          sx={{ textTransform: 'none' }}
        >
          Ver Factura X
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default LiquidacionComisionDetalleDialog;
